import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    searchCardsUrl: String,
    analyzeCardUrl: String,
    cardPricesUrl: String,
    searchCustomersUrl: String,
    createCustomerUrl: String,
    submissionsUrl: String,
    creditBonusPercentage: Number
  }

  static targets = [
    "searchInput", "searchResults",
    "searchPanel", "cartPanel", "searchTab", "cartTab", "cartCount",
    "gameFilter",
    "cameraInput", "cameraStatus", "analysisResult",
    "customerSearch", "customerInput", "customerResults",
    "customerDisplay", "customerName", "customerEmail", "customerCredit",
    "cartItems", "itemCount", "cashTotal", "creditTotal",
    "creditButton", "cashButton",
    "quickCreateModal", "newEmail", "newFirstName", "newLastName", "quickCreateSubmit", "quickCreateError",
    "completeModal", "completeSubmissionNumber", "completePayoutMethod", "completePayoutAmount", "completeProductsCreated", "completePrintLink",
    "processingOverlay"
  ]

  connect() {
    this.cart = []
    this.selectedCustomer = null
    this.activeGameFilter = ""
    this.searchTimeout = null
    this.customerSearchTimeout = null
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  // Mobile panel switching
  showSearchPanel() {
    this.searchPanelTarget.classList.remove("hidden")
    this.cartPanelTarget.classList.add("hidden")
    this.searchTabTarget.classList.add("text-emerald-400", "border-emerald-400")
    this.searchTabTarget.classList.remove("text-slate-400", "border-transparent")
    this.cartTabTarget.classList.remove("text-emerald-400", "border-emerald-400")
    this.cartTabTarget.classList.add("text-slate-400", "border-transparent")
  }

  showCartPanel() {
    this.searchPanelTarget.classList.add("hidden")
    this.cartPanelTarget.classList.remove("hidden")
    this.cartPanelTarget.classList.add("flex")
    this.cartTabTarget.classList.add("text-emerald-400", "border-emerald-400")
    this.cartTabTarget.classList.remove("text-slate-400", "border-transparent")
    this.searchTabTarget.classList.remove("text-emerald-400", "border-emerald-400")
    this.searchTabTarget.classList.add("text-slate-400", "border-transparent")
  }

  // Card search
  searchCards() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this._fetchCards(), 200)
  }

  filterByGame(event) {
    const game = event.currentTarget.dataset.game || ""
    this.activeGameFilter = game

    // Update active button style
    this.gameFilterTargets.forEach(btn => {
      const isActive = (btn.dataset.game || "") === game
      btn.classList.toggle("bg-emerald-500/20", isActive)
      btn.classList.toggle("text-emerald-400", isActive)
      btn.classList.toggle("font-medium", isActive)
      btn.classList.toggle("bg-slate-800", !isActive)
      btn.classList.toggle("text-slate-400", !isActive)
    })

    // Re-search if there's a query
    if (this.searchInputTarget.value.trim()) {
      this._fetchCards()
    }
  }

  async _fetchCards() {
    const query = this.searchInputTarget.value.trim()
    if (!query) {
      this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-12 text-sm">Search for cards to view buy prices</p>'
      return
    }

    this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-8 text-sm">Searching...</p>'

    try {
      let url = `${this.searchCardsUrlValue}?q=${encodeURIComponent(query)}`
      if (this.activeGameFilter) {
        url += `&game=${encodeURIComponent(this.activeGameFilter)}`
      }

      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      const cards = await response.json()
      this._renderCards(cards)
    } catch (e) {
      console.error("Card search failed:", e)
      this.searchResultsTarget.innerHTML = '<p class="text-red-400 text-center py-8 text-sm">Search failed</p>'
    }
  }

  _renderCards(cards) {
    if (cards.length === 0) {
      this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-8 text-sm">No cards found with buy prices</p>'
      return
    }

    this.searchResultsTarget.innerHTML = cards.map(card => {
      const conditionButtons = Object.entries(card.prices).map(([condition, priceData]) => {
        const label = this._escapeHtml(this._conditionLabel(condition))
        const price = (priceData.buy_price_cents / 100).toFixed(2)
        return `
          <button type="button"
                  data-action="intake#addToCart"
                  data-card-id="${card.id}"
                  data-card-name="${this._escapeHtml(card.name)}"
                  data-card-set="${this._escapeHtml(card.card_set || '')}"
                  data-condition="${this._escapeHtml(condition)}"
                  data-offer-cents="${priceData.buy_price_cents}"
                  data-market-price-cents="${priceData.market_price_cents}"
                  class="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-emerald-600 text-slate-300 hover:text-white transition whitespace-nowrap">
            ${label} $${price}
          </button>
        `
      }).join('')

      const marketPrice = Object.values(card.prices)[0]?.market_price_cents
      const marketDisplay = marketPrice ? `$${(marketPrice / 100).toFixed(2)}` : ''

      return `
        <div class="p-3 border-b border-white/5">
          <div class="flex items-start justify-between mb-1.5">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-white">${this._escapeHtml(card.name)}</p>
              <p class="text-xs text-slate-500">${this._escapeHtml(card.card_set || '')}${card.game ? ' · ' + this._escapeHtml(card.game) : ''}${marketDisplay ? ' · Market: ' + marketDisplay : ''}</p>
            </div>
          </div>
          <div class="flex flex-wrap gap-1.5">${conditionButtons}</div>
        </div>
      `
    }).join('')
  }

  // Camera / AI scan
  openCamera() {
    this.cameraInputTarget.click()
  }

  async analyzePhoto() {
    const file = this.cameraInputTarget.files[0]
    if (!file) return

    this.cameraStatusTarget.textContent = "Analyzing..."
    this.analysisResultTarget.classList.add("hidden")

    const formData = new FormData()
    formData.append("image", file)

    try {
      const response = await fetch(this.analyzeCardUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken },
        body: formData
      })

      const data = await response.json()

      if (data.error) {
        this.cameraStatusTarget.textContent = data.error
        return
      }

      this.cameraStatusTarget.textContent = ""
      this._renderAnalysisResult(data)
    } catch (e) {
      console.error("Analysis failed:", e)
      this.cameraStatusTarget.textContent = "Analysis failed"
    }

    // Reset file input
    this.cameraInputTarget.value = ""
  }

  _renderAnalysisResult(data) {
    const { analysis, best_match, prices } = data

    if (!best_match || Object.keys(prices).length === 0) {
      this.analysisResultTarget.innerHTML = `
        <div class="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <p class="text-sm text-amber-400">Card identified: ${this._escapeHtml(analysis.card_name || 'Unknown')}</p>
          <p class="text-xs text-slate-400 mt-1">No matching buylist prices found for this card.</p>
        </div>
      `
      this.analysisResultTarget.classList.remove("hidden")
      return
    }

    const conditionButtons = Object.entries(prices).map(([condition, priceData]) => {
      const label = this._escapeHtml(this._conditionLabel(condition))
      const price = (priceData.buy_price_cents / 100).toFixed(2)
      return `
        <button type="button"
                data-action="intake#addToCart"
                data-card-id="${best_match.id}"
                data-card-name="${this._escapeHtml(best_match.name)}"
                data-card-set="${this._escapeHtml(best_match.card_set || '')}"
                data-condition="${this._escapeHtml(condition)}"
                data-offer-cents="${priceData.buy_price_cents}"
                data-market-price-cents="${priceData.market_price_cents}"
                class="px-2 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white transition whitespace-nowrap">
          ${label} $${price}
        </button>
      `
    }).join('')

    const suggested = analysis.condition ? `Suggested: ${this._escapeHtml(this._conditionLabel(analysis.condition))}` : ''

    this.analysisResultTarget.innerHTML = `
      <div class="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
        <p class="text-sm text-white font-medium">${this._escapeHtml(best_match.name)}</p>
        <p class="text-xs text-slate-400 mt-0.5">${this._escapeHtml(best_match.card_set || '')}${suggested ? ' &middot; ' + suggested : ''}</p>
        <div class="flex flex-wrap gap-1.5 mt-2">${conditionButtons}</div>
      </div>
    `
    this.analysisResultTarget.classList.remove("hidden")
  }

  // Cart management
  addToCart(event) {
    const btn = event.currentTarget
    const cardId = parseInt(btn.dataset.cardId)
    const name = btn.dataset.cardName
    const cardSet = btn.dataset.cardSet
    const condition = btn.dataset.condition
    const offerCents = parseInt(btn.dataset.offerCents)
    const marketPriceCents = parseInt(btn.dataset.marketPriceCents)

    // Check if same card+condition already in cart
    const existing = this.cart.find(i => i.card_id === cardId && i.condition === condition)
    if (existing) {
      existing.quantity++
    } else {
      this.cart.push({
        card_id: cardId,
        name,
        card_set: cardSet,
        condition,
        offer_cents: offerCents,
        market_price_cents: marketPriceCents,
        quantity: 1
      })
    }

    this._renderCart()
    this._updateTotals()

    // Brief flash on button
    btn.classList.add("bg-emerald-600", "text-white")
    setTimeout(() => {
      btn.classList.remove("bg-emerald-600", "text-white")
    }, 200)
  }

  incrementItem(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    if (this.cart[idx]) {
      this.cart[idx].quantity++
      this._renderCart()
      this._updateTotals()
    }
  }

  decrementItem(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    const item = this.cart[idx]
    if (item) {
      if (item.quantity > 1) {
        item.quantity--
      } else {
        this.cart.splice(idx, 1)
      }
      this._renderCart()
      this._updateTotals()
    }
  }

  removeFromCart(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    this.cart.splice(idx, 1)
    this._renderCart()
    this._updateTotals()
  }

  clearCart() {
    this.cart = []
    this.selectedCustomer = null
    this._renderCart()
    this._updateTotals()
    this._showCustomerSearch()
    this.searchInputTarget.focus()
  }

  _renderCart() {
    const totalItems = this.cart.reduce((sum, i) => sum + i.quantity, 0)
    if (this.hasCartCountTarget) this.cartCountTarget.textContent = totalItems

    if (this.cart.length === 0) {
      this.cartItemsTarget.innerHTML = '<p class="text-slate-500 text-center py-8 text-sm">Tap a condition button to add cards</p>'
      return
    }

    this.cartItemsTarget.innerHTML = this.cart.map((item, idx) => `
      <div class="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
        <div class="min-w-0 flex-1">
          <p class="text-sm text-white font-medium truncate">${this._escapeHtml(item.name)}</p>
          <p class="text-xs text-slate-400">
            <span class="inline-block px-1.5 py-0.5 rounded text-xs ${this._conditionBadgeClass(item.condition)}">${this._escapeHtml(this._conditionLabel(item.condition))}</span>
            · $${(item.offer_cents / 100).toFixed(2)} each
          </p>
        </div>
        <div class="flex items-center gap-2 ml-2 shrink-0">
          <button type="button" data-action="intake#decrementItem" data-index="${idx}"
                  class="w-7 h-7 rounded bg-slate-800 hover:bg-slate-700 text-white text-sm flex items-center justify-center">-</button>
          <span class="text-sm text-white w-6 text-center">${item.quantity}</span>
          <button type="button" data-action="intake#incrementItem" data-index="${idx}"
                  class="w-7 h-7 rounded bg-slate-800 hover:bg-slate-700 text-white text-sm flex items-center justify-center">+</button>
          <span class="text-sm text-white font-medium w-16 text-right">$${(item.offer_cents * item.quantity / 100).toFixed(2)}</span>
          <button type="button" data-action="intake#removeFromCart" data-index="${idx}"
                  class="text-slate-500 hover:text-red-400 ml-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    `).join('')
  }

  _updateTotals() {
    const cashTotalCents = this.cart.reduce((sum, i) => sum + (i.offer_cents * i.quantity), 0)
    const bonus = this.creditBonusPercentageValue || 0
    const creditTotalCents = bonus > 0
      ? Math.round(cashTotalCents * (1 + bonus / 100))
      : cashTotalCents
    const totalItems = this.cart.reduce((sum, i) => sum + i.quantity, 0)

    this.itemCountTarget.textContent = totalItems
    this.cashTotalTarget.textContent = `$${(cashTotalCents / 100).toFixed(2)}`
    this.creditTotalTarget.textContent = `$${(creditTotalCents / 100).toFixed(2)}`

    const canPay = this.cart.length > 0 && this.selectedCustomer
    this.creditButtonTarget.disabled = !canPay
    this.cashButtonTarget.disabled = !canPay
  }

  // Customer search
  searchCustomers() {
    clearTimeout(this.customerSearchTimeout)
    this.customerSearchTimeout = setTimeout(() => this._fetchCustomers(), 300)
  }

  async _fetchCustomers() {
    const query = this.customerInputTarget.value.trim()
    if (!query) {
      this.customerResultsTarget.innerHTML = ''
      return
    }

    try {
      const response = await fetch(`${this.searchCustomersUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })
      const customers = await response.json()
      this._renderCustomerResults(customers)
    } catch (e) {
      console.error("Customer search failed:", e)
    }
  }

  _renderCustomerResults(customers) {
    if (customers.length === 0) {
      this.customerResultsTarget.innerHTML = '<p class="text-slate-500 text-xs py-2">No customers found</p>'
      return
    }

    this.customerResultsTarget.innerHTML = customers.map(c => `
      <button type="button"
              data-action="intake#selectCustomer"
              data-customer-id="${c.id}"
              data-customer-name="${this._escapeHtml(c.name)}"
              data-customer-email="${this._escapeHtml(c.email)}"
              data-customer-credit="${c.credit_cents}"
              class="w-full flex items-center justify-between p-2 rounded hover:bg-white/5 text-left text-sm">
        <div>
          <span class="text-white">${this._escapeHtml(c.name)}</span>
          <span class="text-slate-500 text-xs ml-2">${this._escapeHtml(c.email)}</span>
        </div>
        ${c.credit_cents > 0 ? `<span class="text-emerald-400 text-xs">$${(c.credit_cents / 100).toFixed(2)} credit</span>` : ''}
      </button>
    `).join('')
  }

  selectCustomer(event) {
    const btn = event.currentTarget
    this.selectedCustomer = {
      id: parseInt(btn.dataset.customerId),
      name: btn.dataset.customerName,
      email: btn.dataset.customerEmail,
      creditCents: parseInt(btn.dataset.customerCredit)
    }

    this.customerNameTarget.textContent = this.selectedCustomer.name
    this.customerEmailTarget.textContent = this.selectedCustomer.email
    this.customerCreditTarget.textContent = this.selectedCustomer.creditCents > 0
      ? `$${(this.selectedCustomer.creditCents / 100).toFixed(2)} credit`
      : ''

    this.customerSearchTarget.classList.add("hidden")
    this.customerDisplayTarget.classList.remove("hidden")
    this.customerResultsTarget.innerHTML = ''
    this._updateTotals()
  }

  clearCustomer() {
    this.selectedCustomer = null
    this._showCustomerSearch()
    this._updateTotals()
  }

  _showCustomerSearch() {
    this.customerSearchTarget.classList.remove("hidden")
    this.customerDisplayTarget.classList.add("hidden")
    this.customerInputTarget.value = ''
    this.customerResultsTarget.innerHTML = ''
  }

  // Quick-create customer
  showQuickCreate() {
    this.quickCreateModalTarget.classList.remove("hidden")
    this.newEmailTarget.value = ''
    this.newFirstNameTarget.value = ''
    this.newLastNameTarget.value = ''
    this.quickCreateErrorTarget.classList.add("hidden")
    this.newEmailTarget.focus()
  }

  closeQuickCreate() {
    this.quickCreateModalTarget.classList.add("hidden")
  }

  async submitQuickCreate() {
    const email = this.newEmailTarget.value.trim()
    if (!email) {
      this.quickCreateErrorTarget.textContent = "Email is required"
      this.quickCreateErrorTarget.classList.remove("hidden")
      return
    }

    this.quickCreateSubmitTarget.disabled = true
    this.quickCreateSubmitTarget.textContent = "Creating..."

    try {
      const response = await fetch(this.createCustomerUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          email: email,
          first_name: this.newFirstNameTarget.value.trim(),
          last_name: this.newLastNameTarget.value.trim()
        })
      })

      const data = await response.json()

      if (data.error) {
        this.quickCreateErrorTarget.textContent = data.error
        this.quickCreateErrorTarget.classList.remove("hidden")
        return
      }

      // Select the created/found customer
      this.selectedCustomer = {
        id: data.id,
        name: data.name,
        email: data.email,
        creditCents: data.credit_cents
      }

      this.customerNameTarget.textContent = this.selectedCustomer.name
      this.customerEmailTarget.textContent = this.selectedCustomer.email
      this.customerCreditTarget.textContent = this.selectedCustomer.creditCents > 0
        ? `$${(this.selectedCustomer.creditCents / 100).toFixed(2)} credit`
        : ''

      this.customerSearchTarget.classList.add("hidden")
      this.customerDisplayTarget.classList.remove("hidden")
      this.closeQuickCreate()
      this._updateTotals()
    } catch (e) {
      this.quickCreateErrorTarget.textContent = "Failed to create customer"
      this.quickCreateErrorTarget.classList.remove("hidden")
    } finally {
      this.quickCreateSubmitTarget.disabled = false
      this.quickCreateSubmitTarget.textContent = "Create"
    }
  }

  // Payout
  payoutCredit() {
    this._submitIntake("store_credit")
  }

  payoutCash() {
    this._submitIntake("cash")
  }

  async _submitIntake(payoutMethod) {
    if (this.cart.length === 0 || !this.selectedCustomer) return

    // Disable buttons immediately to prevent double-submit
    this.creditButtonTarget.disabled = true
    this.cashButtonTarget.disabled = true
    this.processingOverlayTarget.classList.remove("hidden")

    try {
      // Step 1: Create submission
      const createResponse = await fetch(this.submissionsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          customer_id: this.selectedCustomer.id,
          items: this.cart.map(i => ({
            card_id: i.card_id,
            condition: i.condition,
            quantity: i.quantity,
            offer_cents: i.offer_cents,
            market_price_cents: i.market_price_cents
          }))
        })
      })

      const createData = await createResponse.json()

      if (!createData.success) {
        alert(createData.error || "Failed to create intake submission")
        return
      }

      // Store submission ID for printout link
      this._lastSubmissionId = createData.submission.id

      // Step 2: Complete submission
      const completeUrl = `${this.submissionsUrlValue}/${createData.submission.id}/complete`
      const completeResponse = await fetch(completeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ payout_method: payoutMethod })
      })

      const completeData = await completeResponse.json()

      if (!completeData.success) {
        alert(completeData.error || "Failed to complete intake")
        return
      }

      this._showComplete(completeData)
    } catch (e) {
      console.error("Intake failed:", e)
      alert("Intake submission failed: " + e.message)
    } finally {
      this.processingOverlayTarget.classList.add("hidden")
      this._updateTotals() // Re-enable buttons if cart still has items
    }
  }

  _showComplete(result) {
    this.completeSubmissionNumberTarget.textContent = result.submission_number
    this.completePayoutMethodTarget.textContent = result.payout_method === "store_credit" ? "Store Credit" : "Cash"
    this.completePayoutAmountTarget.textContent = `$${(result.payout_amount_cents / 100).toFixed(2)}`
    this.completeProductsCreatedTarget.textContent = result.products_created

    this.completePrintLinkTarget.href = `${this.submissionsUrlValue}/${this._lastSubmissionId}/printout`

    this.completeModalTarget.classList.remove("hidden")
  }

  newIntake() {
    this.completeModalTarget.classList.add("hidden")
    this.cart = []
    this.selectedCustomer = null
    this._renderCart()
    this._updateTotals()
    this._showCustomerSearch()
    this.searchInputTarget.value = ''
    this.searchResultsTarget.innerHTML = '<p class="text-slate-500 text-center py-12 text-sm">Search for cards to view buy prices</p>'
    this.analysisResultTarget.classList.add("hidden")
    this.cameraStatusTarget.textContent = ''
    this.searchInputTarget.focus()
  }

  // Helpers
  _conditionLabel(condition) {
    const labels = {
      near_mint: "NM",
      light_play: "LP",
      moderate_play: "MP",
      heavy_play: "HP"
    }
    return labels[condition] || condition
  }

  _conditionBadgeClass(condition) {
    const classes = {
      near_mint: "bg-emerald-500/20 text-emerald-400",
      light_play: "bg-blue-500/20 text-blue-400",
      moderate_play: "bg-amber-500/20 text-amber-400",
      heavy_play: "bg-red-500/20 text-red-400"
    }
    return classes[condition] || "bg-slate-500/20 text-slate-400"
  }

  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
